import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaTimes } from "react-icons/fa";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import OtpInput from "react-otp-input";
import Swal from "sweetalert2";
import CustomButton from "../../../components/Button/CustomButton";

import styles from "./CkycOTPModal.module.scss";
import { handlehideContextualLoader, handleShowContextualLoader } from "../../../utils/contextualLoaderTrigger";
import AfterOnboardAPI from "../../../services/AfterOnboardAPI";

export default function CkycOTPModal({ openCkyc, setOpenCkyc, ckycTransactionId, setCkycTransactionId, tempCkycValue }) {
  const [seconds, setSeconds] = useState(60);
  const [otp, setOtp] = useState("");
  const [disableResent, setDisableResent] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  const { accountId } = useSelector((state) => state.user);

  const { verifyCKYC, ckycInitiateAPI } = new AfterOnboardAPI();

  useEffect(() => {
    if (seconds <= 0) return;

    const timer = setTimeout(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [seconds]);

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      Swal.fire({
        icon: "warning",
        text: "Please enter a valid 6-digit OTP",
      });
      return;
    }

    try {
      handleShowContextualLoader();

      const res = await verifyCKYC(ckycTransactionId, otp, accountId);

      localStorage.setItem("ckyc_fullname", res.data.fullName);
      localStorage.setItem("ckyc_ckycReferenceId", res.data.ckycReferenceId);
      localStorage.setItem("reKyc", "1");

      navigate("/ckyc-verification-success", {
        state: res.data,
      });
    } catch (error) {
      console.log(error);

      if (error?.response?.data?.data?.redirectFailureScreen) {
        navigate("/ckyc-verification-failed", {
          state: error?.response?.data?.message,
        });
      } else {
        Swal.fire({
          icon: "error",
          text: error?.response?.data?.message || "Something went wrong",
        });
      }
    } finally {
      handlehideContextualLoader();
    }
  };

  const handleResentAPI = async () => {
    try {
      setOtp("");
      setDisableResent(true);
      const res = await ckycInitiateAPI(accountId);
      if (res?.data?.data?.isCkycFlow) {
        setCkycTransactionId(res?.data?.data?.data?.transactionID);
      }
      setSeconds(60);
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setDisableResent(false);
    }
  };

  const handleCloseAndReset = () => {
    setOpenCkyc(false);
    setOtp("");
    setSeconds(60);
  };

  return (
    <Drawer
      anchor="bottom"
      open={openCkyc}
      onClose={handleCloseAndReset}
      sx={{
        "& .MuiDrawer-paper": {
          maxWidth: 480,
          width: "100%",
          margin: "0 auto",
          left: 0,
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          paddingBottom: "calc(12px + env(safe-area-inset-bottom))",

          [theme.breakpoints.up("sm")]: {
            left: -15,
          },
        },
      }}
    >
      <div className={styles.ckycOtpModal}>
        {/* Close Button */}
        <IconButton className={styles.ckycCloseButton} onClick={handleCloseAndReset} aria-label="Close">
          <FaTimes size={18} />
        </IconButton>

        {/* Header */}
        <div className={styles.ckycOtpHeader}>
          <h3>Enter OTP</h3>

          <p>OTP has been sent to your registered mobile number.</p>
        </div>

        {/* OTP */}
        <div className={styles.ckycOtpInput}>
          <OtpInput
            value={otp}
            onChange={(value) => setOtp(value)}
            numInputs={6}
            inputType="tel"
            shouldAutoFocus
            renderSeparator={<span className={styles.otpSeparator} />}
            renderInput={(props, index) => (
              <input
                {...props}
                autoFocus={index === 0}
                autoComplete="one-time-code"
                className={styles.otpBox}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleVerifyOTP();
                  }
                }}
              />
            )}
          />
        </div>

        {/* Resend */}
        <div className={styles.ckycResend}>
          <p>Didn't receive the OTP?</p>

          {seconds > 0 ? (
            <p className={styles.timer}>Resend OTP in {seconds}s</p>
          ) : (
            <Button variant="text" disabled={disableResent} onClick={handleResentAPI} className={styles.resendButton}>
              Resend OTP
            </Button>
          )}
        </div>

        {/* Verify */}
        <div className={styles.ckycOtpAction}>
          <CustomButton text="Verify OTP" onClick={handleVerifyOTP} />
        </div>
      </div>
    </Drawer>
  );
}
